// The shared timeline cursor — one playhead for the whole console.
//
// Every time-based surface (the stacked charts, the map field, the panel readouts)
// reads this single value instead of keeping its own hover index. That is what makes
// the map and the charts agree: scrub anywhere and everything moves together.
//
// The cursor is an INSTANT (epoch ms), never an array index. The charts run 7-day
// hourly, the field runs by forecast hour and coarsens to 3-hourly past f120 — they
// don't share a sampling, so an index would mean different things to each of them.
// Each consumer maps the instant into its own grid via indexAtTime().
//
// `null` means "no cursor" — nothing is being pointed at or scrubbed.

export type CursorTime = number | null;

type Listener = (t: CursorTime) => void;

let current: CursorTime = null;
const listeners = new Set<Listener>();

/** The instant under the playhead, or null when there is no cursor. */
export function getCursor(): CursorTime {
  return current;
}

/** Move the playhead. No-ops when the value is unchanged, so subscribers are not
 *  woken for redundant sets (pointermove fires far faster than the data changes). */
export function setCursor(t: CursorTime): void {
  const next = t !== null && Number.isFinite(t) ? t : null;
  if (next === current) return;
  current = next;
  // Copy first: a listener may unsubscribe (or re-subscribe) during the notify.
  for (const fn of [...listeners]) fn(current);
}

/** Subscribe to playhead moves. Returns the unsubscribe. */
export function subscribeCursor(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// ---------------------------------------------------------------------------
// Mapping between chart x-labels and instants — and back.
//
// The chart labels are SITE-LOCAL wall clock with no zone ("2026-07-11T14:00"),
// because the point forecast is fetched with timezone=auto. For a long time this
// module read them in the BROWSER's zone instead, which is only the same thing
// when the operator sits in the site's timezone. An operator in Texas looking at
// a West Africa job had the playhead, the confidence strip's past/future
// boundary and the "now" button all disagreeing with the charts by the
// five-to-six hours between the two clocks. (A third reading — plain UTC — is
// what displaced the map-field trace; see meshSeriesAt.)
//
// So the console now has ONE stated convention: the timeline reads in the clock
// of the water being worked. Daylight bands, crew change, "Thursday morning" all
// mean the SITE's Thursday morning. The zone arrives as the forecast's
// utcOffsetSeconds via setLabelZone(), and both directions — parsing labels to
// instants, and formatting instants back into readouts — live here so they
// cannot drift apart again. The map scrubber keeps its separate, explicitly
// labelled UTC readout as the bridge cross-check.

/**
 * The site's offset from UTC in seconds, or null before any forecast has loaded.
 *
 * Null is NOT a silent fallback to something plausible — it is a documented
 * second mode: "these labels are in this browser's zone", which is genuinely
 * true for the auth-free dev harnesses that synthesise their labels locally.
 * The console proper sets the zone on every forecast load.
 *
 * One scalar per forecast, so a DST transition INSIDE the 7-day window is
 * mis-parsed by an hour past the changeover — the same accepted limitation as
 * computeNowIndex() and every other consumer of utcOffsetSeconds.
 */
let labelZoneS: number | null = null;

/** Declare the zone the chart labels are written in. Invalidates the parse
 *  cache — same label text, different instants. */
export function setLabelZone(utcOffsetSeconds: number | null): void {
  const next =
    utcOffsetSeconds !== null && Number.isFinite(utcOffsetSeconds) ? utcOffsetSeconds : null;
  if (next === labelZoneS) return;
  labelZoneS = next;
  timesCache = new WeakMap();
}

/** Parse a chart xLabel ("2026-07-11T14:00") to epoch ms, in the declared label
 *  zone (site-local), or in this browser's zone when none is declared. NaN when
 *  unparseable.
 *
 *  The zone-suffix test comes FIRST: a label that names its own zone
 *  ("...T14:00Z", "...+02:00") is taken at its word, never re-shifted. An
 *  earlier draft matched the digits with an unanchored prefix regex, which
 *  swallowed the suffix and shifted an explicit instant by the site offset —
 *  the mirror image of the parseValidTime bug, from the same shortcut. */
export function timeOfLabel(label: string): number {
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(label);
  const m = hasZone ? null : /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(label);
  if (m === null) {
    const t = Date.parse(label);
    return Number.isFinite(t) ? t : NaN;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const h = Number(m[4]);
  const min = Number(m[5]);
  if (labelZoneS !== null) {
    return Date.UTC(y, mo, d, h, min) - labelZoneS * 1000;
  }
  return new Date(y, mo, d, h, min).getTime();
}

/**
 * An instant broken into the label zone's wall-clock parts, for readouts.
 *
 * The inverse of timeOfLabel, kept beside it deliberately: the transport header
 * and the ruler's playhead chip print these parts, and if they formatted in the
 * browser's zone while the labels parsed in the site's, the chip would name a
 * different hour than the tick under it.
 */
export function zonedParts(t: number): {
  year: number;
  month: number; // 1-12
  weekday: number; // 0 = Sunday, as Date#getDay
  day: number;
  hour: number;
  minute: number;
} {
  if (labelZoneS !== null) {
    const d = new Date(t + labelZoneS * 1000);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      weekday: d.getUTCDay(),
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
    };
  }
  const d = new Date(t);
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    weekday: d.getDay(),
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

// Parsing 168 labels on every pointermove would be wasteful; the label arrays are
// stable references between data updates, so cache against the array itself.
// Reassigned (not cleared — WeakMap cannot be) when the zone changes.
let timesCache = new WeakMap<readonly string[], number[]>();

/** The instants for a label array, parsed once per array identity. */
export function timesOf(labels: readonly string[]): number[] {
  const hit = timesCache.get(labels);
  if (hit !== undefined) return hit;
  const times = labels.map(timeOfLabel);
  timesCache.set(labels, times);
  return times;
}

/** The instant at a data index, or null when out of range / unparseable. */
export function timeAtIndex(labels: readonly string[], i: number): CursorTime {
  const times = timesOf(labels);
  if (i < 0 || i >= times.length) return null;
  const t = times[i];
  return Number.isFinite(t) ? t : null;
}

/** The index whose instant is nearest `t`, or null when there is no cursor, no
 *  labels, or `t` falls outside this series' span. Labels are monotonic (hourly),
 *  so this binary-searches then picks the closer of the two neighbours.
 *
 *  The out-of-span check matters: the tracks do not all cover the same period (a
 *  7-day wind series beside a shorter current series). Without it, a cursor past a
 *  short series' end would pin its crosshair to the last sample and read as data
 *  for an hour that series never had. */
export function indexAtTime(labels: readonly string[], t: CursorTime): number | null {
  if (t === null) return null;
  const times = timesOf(labels);
  const n = times.length;
  if (n === 0) return null;
  if (n === 1) return Math.abs(times[0] - t) === 0 ? 0 : null;

  const first = times[0];
  const last = times[n - 1];
  const step = Math.abs(last - first) / (n - 1);
  const tol = step > 0 ? step / 2 : 0;
  if (t < first - tol || t > last + tol) return null;

  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= t) lo = mid;
    else hi = mid;
  }
  return Math.abs(times[lo] - t) <= Math.abs(times[hi] - t) ? lo : hi;
}
